// import { IPostTargetState, ISocialMediaConnection, PostTargetStatus } from '../../extViewmodels';

import type { IRegEdSubmissionMetadata, RegEdComplianceContentType } from '@Queries/RegEdCompliance/types';
// Import base types from SDK
import type {
	IBulkOperationResult,
	IFileAttachment,
	IRawRichTextContentState,
	IBaseApiModel as SDKBaseApiModel,
	IBaseApiTypeModel as SDKBaseApiTypeModel,
	IOperationResultNoValue as SDKOperationResultNoValue,
} from '../../extViewmodels/sdk/models';

export type ViewTypes = 'SocialMediaPost';
export type ApiModelType = ViewTypes;

// Constrained version of IBaseApiTypeModel to preserve ViewTypes constraint
export interface IBaseApiTypeModel extends Omit<SDKBaseApiTypeModel, '_type'> {
	_type?: ApiModelType;
}

// Constrained version of IBaseApiModel to preserve ViewTypes constraint
export interface IBaseApiModel extends Omit<SDKBaseApiModel, '_type'>, IBaseApiTypeModel {
	// id and lastModifiedDate are already included from SDKBaseApiModel via Omit
}

// Re-export types from SDK for backward compatibility
export type { IBulkOperationResult, IFileAttachment, IRawRichTextContentState };

/**
 * The API's `TechnicalError`. `category` names the error-code enum, so a code is only meaningful
 * alongside it — the same numeric value means something different under another category.
 */
export interface ITechnicalError {
	category?: string;
	errorCode?: number;
	errorMessage?: string;
	source?: string;
	timestamp?: string;
}

// Constrained versions of the SDK operation results: the SDK's omit `error`, which is the only way
// to tell a recoverable rejection apart from the endpoint's other 400s.
export interface IOperationResultNoValue extends SDKOperationResultNoValue {
	error?: ITechnicalError;
}

export interface IOperationResult<T> extends IOperationResultNoValue {
	value?: T;
}

export const asApiError = (e: any) => {
	let error: IOperationResultNoValue = { success: false };
	if (e) {
		if (Object.prototype.hasOwnProperty.call(e, 'systemMessage')) {
			if (Object.prototype.hasOwnProperty.call(e, 'systemCode')) {
				error = e as IOperationResultNoValue;
				error.success = false;
			} else {
				error.systemMessage = e.systemMessage;
			}
		} else if (e instanceof Error) {
			error.systemMessage = e.message;
		} else if (typeof e === 'string') {
			error.systemMessage = e;
		} else if (Object.prototype.hasOwnProperty.call(e, 'toString') && typeof e.toString === 'function') {
			error.systemMessage = e.toString();
		} else {
			error.systemMessage = 'Unexpected error.';
		}

		if (!error.systemCode && (error.systemMessage || '').toLocaleLowerCase() === 'failed to fetch') {
			error.systemCode = -1;
			error.systemMessage = 'Error connecting to Levitate.';
		}
	} else {
		error.systemMessage = 'Unexpected error.';
	}

	return error;
};

export enum HTTPMethod {
	DELETE = 'DELETE',
	GET = 'GET',
	PATCH = 'PATCH',
	POST = 'POST',
	PUT = 'PUT',
}

export interface IApiClientConfig {
	accessToken?: string;
	baseUrl: string;
}

export class ApiClient {
	protected mConfig: IApiClientConfig;

	constructor(config: IApiClientConfig) {
		this.mConfig = config;
	}

	public get baseUrl() {
		return this.mConfig.baseUrl;
	}

	public get accessToken() {
		return this.mConfig.accessToken;
	}

	public get = <T>(path: string) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.GET));
	};

	public put = <T>(path: string, body?: T) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.PUT, body));
	};

	public patch = <T>(path: string, body?: any) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.PATCH, body));
	};

	public delete = <T>(path: string) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.DELETE));
	};

	public post = <T>(path: string, body?: any) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.POST, body));
	};

	protected buildFetchRequest = (path: string, method: HTTPMethod, obj: any = null) => {
		// Check for a special method called FILEPOST, which we will transform to POST but will not convert body to JSON
		const isFormDataBody = obj instanceof FormData;

		// Define headers
		const headers = new Headers();
		if (isFormDataBody) {
			// DO NOT INCLUDE A HEADER FOR FILE POSTS
		} else {
			headers.append('content-type', 'application/json; charset=utf-8');
		}
		headers.append('accept', 'application/json');
		headers.append('Pragma', 'no-cache');
		headers.append('Cache-Control', 'no-cache');
		if (this.mConfig.accessToken) {
			headers.append('Authorization', 'bearer ' + this.mConfig.accessToken);
		}
		const url = `${this.mConfig.baseUrl}/${path}`;
		const config: RequestInit = {
			headers,
			method,
			mode: 'cors',
			redirect: 'follow',
		};

		if (method.toLowerCase() !== 'get' && !!obj) {
			config.body = isFormDataBody ? obj : JSON.stringify(obj);
		}

		const request = new Request(url, config);
		return request;
	};

	protected executeRequest = async <T>(request: Request): Promise<[IOperationResult<T>, IOperationResultNoValue]> => {
		try {
			const response = await fetch(request);
			const apiResponse: IOperationResult<T> = await response.json();
			if (!apiResponse.success) {
				return [null, apiResponse];
			}

			return [apiResponse, null];
		} catch (error) {
			return [null, asApiError(error) as IOperationResultNoValue];
		}
	};
}

export interface IPublicNewsletterView {
	subject: string;
	htmlContent: string;
	/** ISO date when the campaign was sent, or null if it hasn't been sent yet (drafts/scheduled). */
	sentDate: string | null;
	fromName: string;
	accountName: string;
}

export interface ISocialMediaPost extends IBaseApiModel {
	anonymousAccessLink?: string;
	attachments?: IFileAttachment[];
	citation?: string;
	content: IRawRichTextContentState;
	designatedTargets?: IPostTarget[];
	dueDate?: string;
	name?: string;
	targets?: IPostTarget[];
	overlayLogo?: boolean;
	/** Empty unless the post is split; then each platform publishes its own entry and the base publishes nowhere. */
	platformCustomizations?: IPostPlatformCustomization[];
}

export interface IPostPlatformCustomization {
	platform: SocialMediaType;
	content: IRawRichTextContentState;
	attachments?: IFileAttachment[];
}

export interface IAnonymousSocialMediaPost extends ISocialMediaPost {
	logoUrl?: string;
	hideWhiteBackground?: boolean;
	resolvedSignature?: IRawRichTextContentState;
	resolvedDisclosure?: string;
}

export interface IPostTarget {
	provider: SocialMediaType;
	pageId?: string;
	userId?: string;
	state?: IPostTargetState;
}

export interface IPostTargetState {
	status: PostTargetStatus;
	lastAttempted?: string;
	lastConnectionState?: ISocialMediaConnection;
	attempts?: number;
	lastErrorMessage?: string;
}

export enum PostTargetStatus {
	Unknown = 'Unknown',
	Posted = 'Posted',
	Failed = 'Failed',
	Pending = 'Pending',
}

export enum SocialMediaType {
	Unknown = 'Unknown',
	Facebook = 'Facebook',
	Instagram = 'Instagram',
	LinkedIn = 'LinkedIn',
}

export interface ISocialMediaConnection {
	encryptedBlobId?: string;
	expiresAt?: string; // Date
	postTargetDisplayName?: string;
	postTargetId?: string;
	state?: SocialMediaConnectionState;
	type: SocialMediaType;
	userId?: string;
	userName?: string;
}

export const SupportedSocialMediaTypes = [
	SocialMediaType.Facebook,
	SocialMediaType.Instagram,
	SocialMediaType.LinkedIn,
];

export enum SocialMediaConnectionState {
	Connected = 'Connected',
	Disconnected = 'Disconnected',
	NoTarget = 'NoTarget',
	Unknown = 'Unknown',
}

// IFileAttachment and IRawRichTextContentState are now imported from SDK

export enum ComplianceReviewStatus {
	AwaitingReview = 'AwaitingReview',
	Filed = 'Filed',
	Declined = 'Declined',
	Closed = 'Closed',
}

/**
 * One piece of content as the compliance officer's tokenised portal sees it. Every status other than
 * `AwaitingReview` is a page to render, not an error — an officer re-opening their own link after
 * acting on it is expected.
 */
export interface IComplianceReviewView {
	status?: ComplianceReviewStatus;
	contentType?: RegEdComplianceContentType;
	contentName?: string;
	/** Absent unless `status` is `AwaitingReview` — there is nothing left to preview otherwise. */
	post?: IAnonymousSocialMediaPost | null;
	/** The campaign payload, on the same terms as `post`. Exactly one is populated, per `contentType`. */
	email?: IComplianceReviewEmailView | null;
	/** The blog payload, on the same terms as `post`. */
	blog?: IComplianceReviewBlogView | null;
	advisorName?: string;
	companyName?: string;
	scheduledSendDate?: string | null;
	/**
	 * `yyyy-MM-dd` in the content owner's timezone, which the reviewer's browser can't derive. The server
	 * rejects a first-use answer other than this day.
	 */
	scheduledSendDay?: string | null;
	/** AdTrax format EXT IDs the firm accepts, for the format picker. Absent outside `AwaitingReview`. */
	supportedFormats?: string[];
	declineReason?: string | null;
}

/**
 * A campaign as the reviewer sees it. Purpose-built server-side rather than derived from the campaign,
 * so there is nothing internal to strip — and deliberately narrower than the social payload: no
 * attachments, no targets.
 */
export interface IComplianceReviewEmailView {
	subject?: string;
	/** Rendered HTML, sanitized server-side for browser display. */
	body?: string;
	signature?: string;
	citation?: string;
}

/**
 * A blog post as the reviewer sees it — only what the compliance document already shows them.
 */
export interface IComplianceReviewBlogView {
	title?: string;
	/** Rendered HTML, sanitized server-side for browser display. */
	body?: string;
	/** Hero attachment, already a public URL. */
	mainImageUrl?: string;
	/** The hero can be a video, which the compliance document links rather than embeds. */
	mainImageMimeType?: string;
}

export interface IComplianceReviewSubmitRequest {
	metadata: IRegEdSubmissionMetadata;
	reviewLeadTimeOverrideReason?: string;
}

export interface IComplianceReviewDeclineRequest {
	reason: string;
}
